/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import {TreeNodeModel} from '../../models/tree-node-model';
import {PathAlertsEntityModel} from '../../../../shared/path-alerts/models/path-alerts-entity.model';
import {PathModel} from '../../../../shared/path-alerts/models/path.model';
import {EntityTreeModel} from '../../../../shared/entity-tree/models/entity-tree.model';
import {EntityTreeUtilService} from '../../../../shared/entity-tree/services/entity-tree-util.service';
import {PathAlertsModel} from '../../../../shared/path-alerts/models/path-alerts.model';
import {PathTypeEnum} from '../../../../shared/path-alerts/models/path-type.enum';
import {EntityTypeModel} from '../../../../shared/entity-tree/models/entity-type.model';
import {EntityCodeTypeModel} from '../../../../shared/entity-tree/models/entity-code-type.model';
import {EntityTypeEnum} from '../../configuration/v1/entity-type.enum';
import {BusinessSettingsUtil} from '../../services/business-settings-util.service';

@Injectable({
    providedIn: 'root'
})
export class BusinessSettingsTreeMapperService {
    entityPriority: ReadonlyMap<EntityTypeEnum, number> = new Map([
        [EntityTypeEnum.COUNTRIES, 1],
        [EntityTypeEnum.PARTNERS, 2],
        [EntityTypeEnum.SHIP_TO_PARTIES, 3],
        [EntityTypeEnum.SUPPLYING_PACKING_PLANTS, 4],
        [EntityTypeEnum.TRANSPORT_MODES, 5],
        [EntityTypeEnum.TRANSPORT_SERVICES, 6],
        [EntityTypeEnum.LEAD_TIMES, 7],
        [EntityTypeEnum.WARNINGS, 8],
        [EntityTypeEnum.REASONS, 9],
        [EntityTypeEnum.CLAIM_CODES, 10],
        [EntityTypeEnum.DECISION_CODES, 11],
        [EntityTypeEnum.ERROR_CODES, 12],
        [EntityTypeEnum.BI_CATEGORIES, 13],
        [EntityTypeEnum.TECHNICIANS_GROUPS, 14]
    ]);

    constructor(private entityUtil: EntityTreeUtilService,
                private businessSettingsUtil: BusinessSettingsUtil) {
    }

    retrieveBreadcrumbs(tree: TreeNodeModel, entityTree: EntityTreeModel): PathAlertsModel[] {
        const treeNodePaths = this.convertTreeNodeToTreeNodePathMatrix(tree);

        const inactivePathsBreadcrumbs =
                this.retrieveBreadcrumbsType(PathTypeEnum.INACTIVE, true, 'Inactive path(s)', entityTree, treeNodePaths,);

        const unfinishedPathsBreadcrumbs =
                this.retrieveBreadcrumbsType(PathTypeEnum.UNFINISHED, false, 'Unfinished path(s)', entityTree, treeNodePaths,);

        const pathAlerts: PathAlertsModel[] = [];

        if (unfinishedPathsBreadcrumbs.type.count > 0) {
            pathAlerts.push(unfinishedPathsBreadcrumbs);
        }

        if (inactivePathsBreadcrumbs.type.count > 0) {
            pathAlerts.push(inactivePathsBreadcrumbs);
        }

        return pathAlerts;
    }

    /**
     * Given an PathModel, flattens its structure return an array of {@link EntityCodeTypeModel}
     */
    retrievePathCodes(path: PathModel): EntityCodeTypeModel[] {
        if (path == null) {
            return [];
        }

        if (path.parent == null) {
            return [{code: path.entity.code, type: path.type}];
        }

        return this.retrievePathCodes(path.parent)
                .concat({code: path.entity.code, type: path.type});
    }

    private pathPriorityComparator(firstPath: EntityTypeModel[], secondPath: EntityTypeModel[]): number {
        const firstEntity = firstPath.slice(-1)
                .pop();
        const secondEntity = secondPath.slice(-1)
                .pop();

        const firstPriority = this.entityPriority.get(firstEntity.type) ?? Number.MAX_SAFE_INTEGER;
        const secondPriority = this.entityPriority.get(secondEntity.type) ?? Number.MAX_SAFE_INTEGER;

        let pathPriorityDifference = firstPriority - secondPriority;

        if (pathPriorityDifference === 0) {
            pathPriorityDifference = this.businessSettingsUtil.sortingCodeComparator(firstEntity.entity, secondEntity.entity);
        }

        return pathPriorityDifference;
    }

    /**
     * This method retrieves all the breadcrumbs of a given type and of a given tree considering the entity tree model..
     *
     * @param type the type of breadcrumb to retrieve
     * @param hasAccordion if the specific type of breadcrumb should have the accordion feature
     * @param name the name that should be used to identify the breadcrumb type
     * @param treeNodePaths the tree node model.
     * @param entityTree the entity tree model.
     */
    private retrieveBreadcrumbsType(type: PathTypeEnum,
                                    hasAccordion: boolean,
                                    name: string,
                                    entityTree: EntityTreeModel,
                                    treeNodePaths: TreeNodeModel[][]): PathAlertsModel {
        const filteredPaths = this.filterPathsArray(treeNodePaths, type);
        const entityElementPaths = this.convertTreeNodePathMatrixToEntityElementPathMatrix(filteredPaths, entityTree);
        entityElementPaths.sort((a, b) => this.pathPriorityComparator(a, b));

        return this.convertEntityElementPathsMatrixToPathAlertsEntities(entityElementPaths, type, hasAccordion, name);
    }

    /**
     * Filters the treeNodePaths and returns only the nodes with a specific type of path.
     * If a path with the given condition is present, then the sub-path (from the root to that specific entity)
     * is considered that type breadcrumb.
     *
     * @param treeNodePaths the {@link TreeNodeModel[][]} to filter
     * @param pathType the type of paths to filter
     */
    private filterPathsArray(treeNodePaths: TreeNodeModel[][], pathType: PathTypeEnum): TreeNodeModel[][] {
        // because we have a tree node path matrix of all complete paths,
        // if an entity is inactive/unfinished but has 2 children, we only want to consider 1 path to that same parent entity
        // The map is used for that.
        const paths = new Map<String, TreeNodeModel[]>();

        treeNodePaths.forEach((path) => {
            const subPathAccumulator: TreeNodeModel[] = [];
            path.forEach((element) => {
                subPathAccumulator.push(element);

                let condition: boolean;
                if (pathType === PathTypeEnum.INACTIVE) {
                    condition = element.pathState?.inactivePath?.isOrigin;
                } else {
                    condition = element.pathState?.unfinishedPath?.isOrigin;
                }


                if (condition) {
                    const key = subPathAccumulator.map((x) => x.code)
                            .join(',');
                    if (!paths.has(key)) {
                        paths.set(key, [...subPathAccumulator]);
                    }
                }
            });
        });

        return Array.from(paths.values());
    }

    /**
     * This method converts a TreeNodeModel into an array of TreeNodeModel.
     * Each entry of the array is a complete path of the given tree. The first element of each entry is the leaf, the last is the root.
     * @param node the node.
     * @param accumulator the path accumulator.
     * @param path the array of paths.
     */
    private recursiveTreeToPaths(node: TreeNodeModel, accumulator: TreeNodeModel[][], path: TreeNodeModel[] = []): void {
        if (node != null) {
            if (node.relationTree == null || node.relationTree.length === 0) {
                accumulator.push(path);
            } else {
                node.relationTree.forEach((child) => {
                    path.push(child);
                    this.recursiveTreeToPaths(child, accumulator, [...path]);
                    path.pop();
                });
            }
        }
    }

    private convertTreeNodeToTreeNodePathMatrix(tree: TreeNodeModel): TreeNodeModel[][] {
        const result: TreeNodeModel[][] = [];
        this.recursiveTreeToPaths(tree, result);
        return result;
    }

    private convertTreeNodePathMatrixToEntityElementPathMatrix(
            pathsMatrix: TreeNodeModel[][], entityTree: EntityTreeModel): EntityTypeModel[][] {
        const entityElementPathMatrix: EntityTypeModel[][] = [];

        pathsMatrix.forEach((entry) => {
            const path: EntityTypeModel[] = [];
            entry.forEach((element) => {
                const entityElement = this.entityUtil.getEntityConfiguration(element.type, entityTree)
                        .pool.find((item) => item.code === element.code);
                if (entityElement !== null) {
                    path.push({entity: entityElement, type: element.type});
                }
            });
            entityElementPathMatrix.push(path);
        });

        return entityElementPathMatrix;
    }

    private convertEntityElementPathsMatrixToPathAlertsEntities(
            paths: EntityTypeModel[][], type: PathTypeEnum, hasAccordion: boolean, name: string): PathAlertsModel {
        const mapFinal = new Map<String, PathAlertsEntityModel>();

        paths.forEach((path) => {
            const element = [...path].pop()?.entity;

            if (element != null) {
                const pathModel = this.mapEntityElementPathsToPathModel(path.reverse());

                if (mapFinal.has(element.code)) {
                    const value: PathAlertsEntityModel = mapFinal.get(element.code);
                    value.paths.push(pathModel);
                    mapFinal.set(element.code, value);

                } else {
                    const displayLabel = element.breadcrumbLabel ?? `${element.name} ${element.code}`;
                    mapFinal.set(element.code, {
                        value: element,
                        displayLabel: displayLabel,
                        paths: [pathModel]
                    });
                }
            }
        });

        const entities = Array.from(mapFinal.values());

        // the total of paths is given by the length of paths of each entity path
        const total = entities.map((entity) => entity.paths.length)
                .reduce((acc, cur) => acc + cur, 0);

        return {
            type: {
                code: type,
                name: name,
                count: total
            },
            entities: entities,
            hasAccordion: hasAccordion
        } as PathAlertsModel;
    }

    private mapEntityElementPathsToPathModel = (items: EntityTypeModel[]): PathModel => {
        let root: PathModel = null;
        let actualNode: PathModel = null;

        items.forEach((item) => {
            const displayLabel = item.entity?.breadcrumbLabel ?? `${item.entity?.name}`;
            const entity: PathModel = {entity: item.entity, displayLabel: displayLabel, type: item.type};
            if (root == null) {
                root = entity;
            } else {
                actualNode.parent = entity;
            }
            actualNode = entity;
        });

        return root;
    };

}
